import React from 'react'
import { leagueIds } from 'src/constants'
import { networkLogos } from 'src/constants/networkLogos'
import { isUSFreeTrial } from 'src/modules/utils/constants'

export const coxData = {
  competitor: 'Cox Cable',
  competitorColor: '#FFFFFF',
  heroData: {
    title: 'Looking for the best streaming service?',
    subtitle:
      'Compare Fubo streaming service and Cox Communications cable TV service on price, features, channels and add-ons to see which is better for you.',
  },
  generalCompareData: {
    heading: 'Compare Fubo vs. Cox Communications Cable TV',
    subheading:
      "Here's a general look at both to help you determine which is the best option for live TV entertainment.",
    listItems: ['Channel Count', 'Cloud DVR', 'Simultaneous Streams', 'Starting Price'],
    fuboList: ['200+', 'Unlimited hours', 'Up to 10', '$73.99/mo.*'],
    competitorList: ['140+', 'Limited storage (additional monthly fee)', 'Up to 5', '$125.00/mo.*'],
    note: '*Prices may vary with taxes and fees. Prices subject to change. Cox Communications pricing may include additional equipment rental and broadcast TV fees.',
    fuboLogoText: 'Pro plan',
    competitorLogoText: 'Contour TV Preferred',
  },
  prosConsData: {
    heading: 'Pros and Cons: Fubo vs. Cox Communications',
    content: (
      <>
        <p>
          When it comes to watching live TV, Fubo and Cox Communications offer two very different
          approaches. Fubo is a modern, sports-first streaming service designed for flexibility and
          affordability, while Cox Communications is a traditional cable service with a range of
          channels but higher costs and additional fees.
        </p>
        <p>
          Fubo is a premium streaming service with a sports-first approach, offering 200+ live
          channels including 50+ sports networks and extensive local sports coverage. It delivers
          Unlimited Cloud DVR storage, up to 10 simultaneous streams, and cutting-edge features like
          4K HDR streaming and Multiview, which lets you watch up to 4 channels at once on supported
          devices. Eligible plans also include ESPN Unlimited, providing access to additional live
          events and original content. Fubo's no-contract model allows users to cancel anytime
          without penalties, making it ideal for cord cutters seeking flexibility.
        </p>
        <p>
          Cox Communications' Contour TV Preferred cable plan, on the other hand, provides a
          traditional cable TV experience with 140+ channels and some DVR capabilities that require
          additional monthly fees. It comes with significant limitations, including mandatory cable
          box equipment, installation requirements and additional fees for equipment rental, DVR
          service, and broadcast TV charges that can drive up monthly costs. While Cox
          Communications offers some regional sports networks, it either lacks or requires an
          upgrade for the comprehensive sports coverage that Fubo provides, including ACC Network,
          SEC Network, Big Ten Network, NFL Network, beIN Sports, Formula 1 Channel, and PFL. The
          service also lacks modern streaming flexibility, with limited concurrent streams and
          viewing primarily tied to cable box locations.
        </p>
      </>
    ),
  },
  plansData: {
    heading: 'Compare Fubo Pro vs. Cox Contour TV Preferred',
    subheading:
      "Let's take a look at the differences between the popular Fubo Pro plan and Cox Contour TV Preferred.",
    tableData: {
      Plan: ['Pro plan', 'Contour TV Preferred'],
      'Live Channels': ['200+', '140+'],
      'Monthly Price': ['$73.99', '$125.00+ (with additional fees)'],
      'Equipment Fees': ['$0', '$8.50/mo per Contour box'],
      'Hours of DVR storage': ['Unlimited', 'DVR service available (additional fee)'],
      'Free Trial': [isUSFreeTrial ? 'Yes' : 'No', 'No'],
      'Multiple active streams': ['Up to 10', 'Limited with Contour TV app'],
      '4K HDR streaming': [true, false],
      'No contract required': [true, false],
    },
    note: '*Prices may vary with taxes and fees. Prices subject to change. Cox Communications may require additional equipment rental, installation fees, and broadcast TV fees.',
  },
  channelsData: {
    heading: 'Compare Fubo Channels vs. Cox Contour TV Preferred Channels',
    subheading:
      "This channel comparison chart focuses on what's available among top-watched channels in the Fubo Pro plan vs. Cox Contour TV Preferred.",
    networksData: [
      { network: networkLogos.abc, fubo: true, competitor: true },
      { network: networkLogos.cbs, fubo: true, competitor: true },
      // { network: networkLogos.nbc, fubo: true, competitor: true },
      { network: networkLogos.fox, fubo: true, competitor: true },
      { network: networkLogos.espn, fubo: true, competitor: true },
      { network: networkLogos.acc, fubo: true, competitor: false },
      { network: networkLogos.sec, fubo: true, competitor: false },
      { network: networkLogos.fs1, fubo: true, competitor: true },
      { network: networkLogos.bigten, fubo: true, competitor: false },
      { network: networkLogos.foxnews, fubo: true, competitor: true },
      { network: networkLogos.bein, fubo: true, competitor: false },
      { network: networkLogos.nflnetwork, fubo: true, competitor: false },
    ],
  },
  completedChannelsData: {
    heading: 'Complete Channel Comparison',
    subheading:
      'See how the live channel lineups compare between Fubo Pro and Cox Contour TV Preferred.',
    content: (
      <>
        <p>
          When it comes to watching live TV and sports, it's clear that Fubo Pro and Cox Contour TV
          Preferred offer very different approaches. While both services provide a mix of sports,
          entertainment and news channels — the differences in their offerings and value
          propositions are significant.
        </p>
        <p>
          Fubo Pro dominates with its 200+ channels, including 50+ sports networks and channels like
          ACC Network, SEC Network, Big Ten Network, NFL Network, beIN Sports, Formula 1 Channel,
          and PFL plus a lineup of regional sports networks for local team coverage. Sports fans
          won't find those channels on Cox Contour TV Preferred. Fubo Pro is the ultimate choice for
          sports enthusiasts, offering superior coverage of local sports, major leagues and
          international competitions. Beyond sports, Fubo Pro includes popular entertainment
          channels like Bravo, Disney Channel and Nickelodeon, plus Spanish-language channels like
          Telemundo, making it perfect for a variety of households.
        </p>
        <p>
          Cox Contour TV Preferred offers a traditional cable experience with 140+ channels
          including major broadcast networks like ABC, CBS, FOX, and NBC. However, it falls short in
          sports coverage, lacking premium networks that can be found in Fubo Pro. Cox
          Communications' cable TV model requires equipment rentals, installation fees, and
          additional DVR service fees that increase monthly costs. Unlike Fubo's Unlimited Cloud DVR
          storage, Cox Communications' DVR comes in various capacities with storage limitations and
          offers limited concurrent streams. For cord cutters seeking value, flexibility and
          comprehensive sports coverage, Fubo Pro is the clear winner.
        </p>
      </>
    ),
  },
  faqData: {
    qaData: [
      {
        id: 'fubo-faq-1',
        question: 'What channels does Fubo Pro have vs. Cox Contour TV Preferred?',
        answer: (
          <p>
            Fubo Pro includes sports channels like ACC Network, SEC Network, Big Ten Network, NFL
            Network, beIN Sports, Formula 1 Channel, and PFL, which Cox Contour TV Preferred does
            not offer. Fubo also has a broader entertainment lineup with channels like Bravo and
            Nickelodeon.
          </p>
        ),
      },
      {
        id: 'fubo-faq-2',
        question: 'Is Fubo Pro better than Cox Contour TV Preferred for sports?',
        answer: (
          <p>
            Yes, Fubo Pro is better for sports. It offers more sports channels, including exclusive
            options like ACC Network, SEC Network, Big Ten Network, NFL Network, beIN Sports, and
            Formula 1 Channel, as well as extensive local sports coverage and regional sports
            networks.
          </p>
        ),
      },
      {
        id: 'fubo-faq-3',
        question: 'How much does Fubo Pro cost vs. Cox Contour TV Preferred?',
        answer: (
          <p>
            Fubo Pro costs $73.99 per month, while Cox Contour TV Preferred costs $125.00+ per
            month, plus $8.50/mo per Contour box and additional fees for DVR service and other
            charges. Fubo offers better value per channel and includes features like unlimited DVR
            storage and 4K HDR streaming at no extra cost.
          </p>
        ),
      },
      {
        id: 'fubo-faq-4',
        question: 'Does Fubo Pro require a contract?',
        answer: (
          <p>
            No, Fubo Pro does not require a contract. You can cancel anytime without penalties,
            unlike Cox Contour TV Preferred, which may require a commitment and charge early
            termination fees.
          </p>
        ),
      },
      {
        id: 'fubo-faq-5',
        question: 'Can I stream Fubo Pro on multiple devices?',
        answer: (
          <p>
            Yes, Fubo Pro allows up to 10 simultaneous streams, making it ideal for households with
            multiple viewers. Cox Contour TV Preferred has limited concurrent streaming
            capabilities.
          </p>
        ),
      },
    ],
  },
  // Show multiple sports (NFL, NBA, NHL, College Basketball, College Football)
  matchtickerSportId: null, // Not needed when using multiple leagues
  matchtickerLeagueName: 'Multiple Sports',
  matchtickerLeagueId: [
    leagueIds.NFL,
    leagueIds.NBA,
    leagueIds.NHL,
    leagueIds["Men's NCAA Basketball"],
    leagueIds['NCAA Football'],
  ],
}
